// game/shared/src/doodad-corridor-repair.ts
//
// Sealed-corridor REPAIR pass (2026-07-06). Ground truth: every area KTS marks
// walkable (pathing.bin bit 0 clear) and connects to spawn must stay reachable
// in our game. Decorative doodads — rocks re-radiused for our wider meshes AND
// tree trunks (r 0.4–0.9) — can pair up with collision gaps narrower than a
// player, sealing corridors KTS keeps open (2026-07-06 audit: 497 sealed cells
// in 130 clusters, three live player reports).
//
// Local pair heuristics can't fix this (widening pair spacing in the greedy
// resolver MOVED the seals rather than removing them — measured 497→653). This
// pass instead operates on the real metric: flood-fill passability with the
// FULL solid-doodad mask applied (rocks + tree trunks + immovables — the same
// model the runtime collision and the KTS-parity gate use; painting only rocks
// left tree-walled pockets invisible here while the gate flagged them), diff
// against the KTS flood, and DROP the specific sealing doodads (rocks first;
// move-based repair measurably created new seals elsewhere).
//
// Pure + deterministic (no RNG); runs at the same shared chokepoint as the
// placement resolver so client render, server collision, and the navmesh bake
// all see identical repaired rows.

import { resolveDoodad } from "./doodad-registry";
import { canyonObstacle, doodadObstacle, doodadRowId, LANDMARK_OBSTACLES, type StaticObstacle } from "./obstacles";
import { generateCanyonPlacements, type CanyonRegion } from "./canyon-placement";
import { RESOURCE_RADII } from "./collision-core";
import { CLIFF_PLATEAU_LEVELS } from "./cliff-edges";

export type RepairRow = {
  kind: "unit" | "doodad";
  type: string;
  x: number;
  y: number; // world-Z plane coordinate (KTS export convention)
  z: number; // KTS height (pass-through; doodadRowId hashes it)
  rot?: number;
  scale?: number;
};

export type RepairWorld = {
  /** 256×256 uint8, bit 0 = KTS no-pathing. */
  pathing: Uint8Array;
  /** 257×257 float32 raw terrain heights. */
  heights: Float32Array;
  /** Post-flip team-0 spawn (flood anchor). */
  spawn?: { x: number; z: number };
  /** regions.json rows — canyon-framing obstacles join the immovable mask so
   *  composite canyon+doodad seals are visible (currently an empty set while
   *  CANYON_FRAMING_ENABLED is false, but plumbed so re-enabling the framing
   *  can't silently diverge this pass from the runtime collision model). */
  regions?: CanyonRegion[];
  /** 256×256 uint16 cliff levels — enables the small-plateau rock PURGE: any
   *  rock whose collider touches a small plateau's surface is dropped outright.
   *  A tiny hilltop's whole top is a transit lane between its ramps; the
   *  sealed-corridor flood CANNOT see a blocked crossing there (rock-occupied
   *  cells read as "legitimately lost ground" while both halves stay reachable
   *  via their own ramps — the SouthRezzShrine east↔west crossing, Aviv
   *  2026-07-06), and nudging has nowhere to go on a small top. */
  cliffLevels?: Uint16Array;
};

const CD = 256, VD = 257;
const PLAYER_R = 0.55;
const MAX_STEP = 1.6;      // walkable terrain step between adjacent cells
const MAX_ROUNDS = 16;
const MIN_CLUSTER = 2;     // ignore 1-cell slivers
const MAX_DROPS = 900;     // sanity bound; measure after the wall-breaker + no-go purge
// "Narrow hill": a plateau component this small is all transit lane (matches
// doodad-placement's small-plateau anchor rule).
const SMALL_PLATEAU_MAX_CELLS = 140;

const isRock = (t: string): boolean => /Rock|Rubble|Stone/i.test(t);
const isRampLevel = (l: number): boolean => l !== 0 && !CLIFF_PLATEAU_LEVELS.has(l);

/** The rock NO-GO mask: every RAMP cell (+1-cell dilation for the mouths) plus
 *  the full surface of small plateau components (≤ SMALL_PLATEAU_MAX_CELLS).
 *  Aviv's standing rule ("avoid ANY rock doodads on narrow hills/ramps") applied
 *  literally after three rounds of blocked-ramp reports burned down every
 *  narrower carve-out: the narrow-run≤4 gate missed wide-ish ramps, and
 *  occupied-band crossings are invisible to the sealed-corridor flood by
 *  construction. Rocks simply do not belong on ramps or small hilltops. */
function rockNoGoMask(cliffLevels: Uint16Array): Uint8Array {
  const core = new Uint8Array(CD * CD);
  // 1. All ramp cells.
  for (let i = 0; i < CD * CD; i++) if (isRampLevel(cliffLevels[i]!)) core[i] = 1;
  // 2. Small plateau components.
  const seen = new Uint8Array(CD * CD);
  for (let r = 0; r < CD; r++) {
    for (let c = 0; c < CD; c++) {
      const i0 = r * CD + c;
      const l0 = cliffLevels[i0]!;
      if (seen[i0] || l0 === 0 || !CLIFF_PLATEAU_LEVELS.has(l0)) continue;
      const comp: number[] = [];
      const stack = [i0];
      seen[i0] = 1;
      while (stack.length) {
        const i = stack.pop()!;
        comp.push(i);
        const cc = i % CD, rr = (i / CD) | 0;
        for (const [dc, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
          const nc = cc + dc, nr = rr + dr;
          if (nc < 0 || nc >= CD || nr < 0 || nr >= CD) continue;
          const ni = nr * CD + nc;
          if (!seen[ni] && cliffLevels[ni] === l0) { seen[ni] = 1; stack.push(ni); }
        }
      }
      if (comp.length <= SMALL_PLATEAU_MAX_CELLS) for (const i of comp) core[i] = 1;
    }
  }
  // 3. 1-cell dilation — ramp/hilltop MOUTHS (rocks flanking the entry pinch it
  //    against terrain just as effectively as rocks on the ramp itself).
  const mask = new Uint8Array(CD * CD);
  for (let r = 0; r < CD; r++)
    for (let c = 0; c < CD; c++)
      if (core[r * CD + c])
        for (let dr = -1; dr <= 1; dr++)
          for (let dc = -1; dc <= 1; dc++) {
            const nr = r + dr, nc = c + dc;
            if (nr >= 0 && nr < CD && nc >= 0 && nc < CD) mask[nr * CD + nc] = 1;
          }
  return mask;
}

// The REAL collider for a decorative-doodad row: radius AND recentred position
// (the render offsets asymmetric-pivot rock meshes, and the collider follows —
// painting at the raw row position under-covered by up to ~1.5u and the repair
// "cleared" pockets that stayed sealed by the true colliders). Covers ALL solid
// doodads, not just rocks: tree trunks (r 0.4–0.9) pinch corridors exactly like
// small rocks, and painting only rocks left tree-walled pockets looking open to
// this pass while the runtime collision (and the KTS-parity gate) sealed them.
function doodadCollider(o: RepairRow): StaticObstacle | null {
  if (o.kind !== "doodad") return null;
  const res = resolveDoodad(o.type, doodadRowId(o));
  const variant = res.kind === "mesh" ? res.variant : undefined;
  return doodadObstacle(o, variant);
}

/**
 * Repair doodad-sealed corridors. Returns a NEW rows array with the sealing
 * decorative doodads dropped. Rocks are dropped preferentially; trees only
 * fall for pockets no guilty rock can open. Other rows pass through.
 */
export function repairSealedCorridors<T extends RepairRow>(rows: T[], world: RepairWorld): T[] {
  const spawn = world.spawn ?? { x: 22.8, z: 24.1 };
  const cellH = (c: number, r: number): number => {
    const h = (cc: number, rr: number): number =>
      world.heights[Math.min(VD - 1, rr) * VD + Math.min(VD - 1, cc)]!;
    return (h(c, r) + h(c + 1, r) + h(c, r + 1) + h(c + 1, r + 1)) / 4;
  };
  const ktsOpen = (c: number, r: number): boolean => !(world.pathing[r * CD + c]! & 1);

  const flood = (extraBlocked: Uint8Array | null): Uint8Array => {
    const seen = new Uint8Array(CD * CD);
    const sc = Math.floor(spawn.x), sr = Math.floor(spawn.z);
    const stack = [sr * CD + sc];
    seen[sr * CD + sc] = 1;
    while (stack.length) {
      const i = stack.pop()!;
      const c = i % CD, r = (i / CD) | 0;
      for (const [dc, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const nc = c + dc, nr = r + dr;
        if (nc < 1 || nc >= CD - 1 || nr < 1 || nr >= CD - 1) continue;
        const ni = nr * CD + nc;
        if (seen[ni] || !ktsOpen(nc, nr)) continue;
        if (extraBlocked && extraBlocked[ni]) continue;
        if (Math.abs(cellH(nc, nr) - cellH(c, r)) > MAX_STEP) continue;
        seen[ni] = 1;
        stack.push(ni);
      }
    }
    return seen;
  };
  const ktsReach = flood(null);

  // Immovable solids: harvestable resource nodes (gameplay objects, authored
  // positions stay; players deplete through them) + bespoke landmarks + canyon
  // framing. Included in the passability mask so seals against them are SEEN;
  // never candidates for dropping.
  const immovable: Array<{ x: number; z: number; r: number }> = [
    ...rows
      .filter((o) => o.kind === "unit" && RESOURCE_RADII[o.type] !== undefined)
      .map((o) => ({ x: o.x, z: o.y, r: RESOURCE_RADII[o.type]! })),
    ...LANDMARK_OBSTACLES,
    ...generateCanyonPlacements(world.regions ?? [])
      .map((p) => canyonObstacle(p))
      .filter((o): o is StaticObstacle => o !== null),
  ];

  // Droppable colliders, hoisted: positions never change (drop-only pass), so
  // resolve each doodad's true collider once. Index-aligned with `rows`.
  const colliders = new Map<number, StaticObstacle>();
  for (let idx = 0; idx < rows.length; idx++) {
    const ob = doodadCollider(rows[idx]!);
    if (ob) colliders.set(idx, ob);
  }

  const dropped = new Set<number>();

  // Rock WALL-BREAKER (pre-pass): KTS authored these rock positions for much
  // smaller SC2 props — gaps a unit walked through existed in the original.
  // Our re-scaled rocks fuse into continuous collision WALLS (chains of circles
  // with sub-player gaps), turning plateau interiors into labyrinths (Aviv at
  // (77,132), 2026-07-06: a 4-rock overlapping wall blocked west with only a
  // hidden 1-cell lane around). Restore the KTS intent: within every chain of
  // rocks whose collision circles come within WALKABLE_GAP of each other, keep
  // the largest rocks and drop any rock closer than WALKABLE_GAP to an already-
  // kept one — every surviving pair is walkable-between by construction.
  const WALKABLE_GAP = 1.3;
  let wallBroken = 0;
  {
    const rockIdx = [...colliders.keys()].filter((i) => isRock(rows[i]!.type));
    // Sort by radius DESC (stable by index) — greedy keep-largest.
    const order = rockIdx.sort((a, b) => (colliders.get(b)!.r - colliders.get(a)!.r) || (a - b));
    const kept: number[] = [];
    for (const idx of order) {
      const ob = colliders.get(idx)!;
      let tooClose = false;
      for (const k of kept) {
        const kb = colliders.get(k)!;
        if (Math.hypot(ob.x - kb.x, ob.z - kb.z) - ob.r - kb.r < WALKABLE_GAP) { tooClose = true; break; }
      }
      if (tooClose) { dropped.add(idx); wallBroken++; }
      else kept.push(idx);
    }
  }

  // Rock NO-GO PURGE (pre-pass): drop every rock whose collider touches a ramp
  // cell (+mouths) or a small plateau's surface. Harvestables/trees stay
  // (gameplay / scenery with thin trunks); ramps and hilltop transit lanes open
  // by construction.
  let plateauPurged = 0;
  if (world.cliffLevels) {
    const noGo = rockNoGoMask(world.cliffLevels);
    for (const [idx, ob] of colliders) {
      if (dropped.has(idx) || !isRock(rows[idx]!.type)) continue;
      const c0 = Math.max(0, Math.floor(ob.x - ob.r)), c1 = Math.min(CD - 1, Math.floor(ob.x + ob.r));
      const r0 = Math.max(0, Math.floor(ob.z - ob.r)), r1 = Math.min(CD - 1, Math.floor(ob.z + ob.r));
      let hit = false;
      for (let rr = r0; rr <= r1 && !hit; rr++)
        for (let cc = c0; cc <= c1 && !hit; cc++) {
          if (!noGo[rr * CD + cc]) continue;
          const nx = Math.max(cc, Math.min(ob.x, cc + 1));
          const nz = Math.max(rr, Math.min(ob.z, rr + 1));
          if (Math.hypot(ob.x - nx, ob.z - nz) < ob.r + 0.1) hit = true;
        }
      if (hit) { dropped.add(idx); plateauPurged++; }
    }
  }

  for (let round = 0; round < MAX_ROUNDS; round++) {
    // Blocked mask + per-cell DROPPABLE blocker indices from current survivors.
    const blocked = new Uint8Array(CD * CD);
    const cellBlockers = new Map<number, number[]>();
    const paint = (x: number, z: number, r: number, idx: number | null): void => {
      const reach = r + PLAYER_R;
      const c0 = Math.max(0, Math.floor(x - reach)), c1 = Math.min(CD - 1, Math.floor(x + reach));
      const r0 = Math.max(0, Math.floor(z - reach)), r1 = Math.min(CD - 1, Math.floor(z + reach));
      for (let rr = r0; rr <= r1; rr++)
        for (let cc = c0; cc <= c1; cc++)
          if (Math.hypot(x - (cc + 0.5), z - (rr + 0.5)) < reach) {
            blocked[rr * CD + cc] = 1;
            if (idx !== null) {
              const list = cellBlockers.get(rr * CD + cc) ?? [];
              list.push(idx);
              cellBlockers.set(rr * CD + cc, list);
            }
          }
    };
    for (const s of immovable) paint(s.x, s.z, s.r, null);
    for (const [idx, ob] of colliders) {
      if (dropped.has(idx)) continue;
      paint(ob.x, ob.z, ob.r, idx);
    }

    const ours = flood(blocked);
    // Sealed-open cells: KTS-connected, not ours-connected, not physically
    // covered by a solid (those are legitimately lost ground).
    const sealed: number[] = [];
    for (let i = 0; i < CD * CD; i++)
      if (ktsReach[i] && !ours[i] && !blocked[i]) sealed.push(i);
    if (sealed.length < MIN_CLUSTER) break;

    // Guilty = MOVABLE blockers painting cells within 2 cells of a sealed cell
    // (the corridor walls). Immovables and terrain contribute to seals but only
    // rocks get moved.
    const guilty = new Set<number>();
    for (const i of sealed) {
      const c = i % CD, r = (i / CD) | 0;
      for (let dr = -2; dr <= 2; dr++)
        for (let dc = -2; dc <= 2; dc++) {
          const nc = c + dc, nr = r + dr;
          if (nc < 0 || nc >= CD || nr < 0 || nr >= CD) continue;
          for (const b of cellBlockers.get(nr * CD + nc) ?? []) guilty.add(b);
        }
    }
    if (guilty.size === 0) break; // sealed purely by terrain/immovables — nothing we may drop

    // Drop-only repair: removing a blocker can never create a NEW seal (moves
    // can and did — measured 474→560 when retreating rocks re-pinched elsewhere;
    // this pass converges monotonically instead). Guilty ROCKS go first,
    // smallest-first, bounded per round; trees are only dropped in rounds where
    // no rock is guilty anywhere (tree-only walls), so tree loss stays minimal —
    // a pocket whose rock drop opens it never costs its trees. Interior blockers
    // stop being guilty once their pocket opens, so drops stay minimal.
    const guiltyRocks = [...guilty].filter((i) => isRock(rows[i]!.type));
    const pool = guiltyRocks.length > 0 ? guiltyRocks : [...guilty];
    const order = pool.sort((a, b) => (colliders.get(a)!.r - colliders.get(b)!.r) || (a - b));
    let touched = 0;
    for (const idx of order.slice(0, 60)) {
      if (dropped.size >= MAX_DROPS) break;
      dropped.add(idx);
      touched++;
    }
    if (touched === 0) break;
  }

  if (dropped.size > 0) {
    // One boot-time line so density loss is visible in ops logs, mirroring the
    // navmesh bake's own summary line.
    const rocks = [...dropped].filter((i) => isRock(rows[i]!.type)).length;
    console.log(
      `[doodads] corridor repair dropped ${dropped.size} corridor-sealing doodads ` +
      `(${rocks} rocks, ${dropped.size - rocks} trees/other; ${wallBroken} wall-breaker, ${plateauPurged} ramp/plateau no-go)`,
    );
    return rows.filter((_, i) => !dropped.has(i));
  }
  return rows;
}
